import { useQuery } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, IconRefresh, Notice, Plate, TabPanel, Tabs } from "@wilco/ui";
import { useState } from "react";
import { formatDate, formatNumber, t } from "../i18n.ts";
import { ScanBar } from "../scan/ScanBar.tsx";
import { stockApi } from "./api.ts";
import { todayQuery } from "./queries.ts";
import { errorNotice, type NoticeState } from "./ShelfPage.tsx";
import type { IssueCard, ReceiptCard } from "./types.ts";

const route = getRouteApi("/app/stock/today");

export type TodayTab = "receive" | "issue" | "count";

/**
 * «Склад сьогодні» (chmk_floor, warehouse_today): очереди кладовщика
 * «Основного». «Прийняти» по ожидаемым поставкам, «Видати на склад» по
 * заявкам с жёстким резервом в порядке даты потребности, «Підрахунок» по
 * дате подсчёта. Скан полки ведёт на «Полицю» в режиме вкладки.
 */
export function TodayPage() {
  const { tab = "receive" } = route.useSearch();
  const navigate = useNavigate();
  const today = useQuery(todayQuery);
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const scan = async (code: string) => {
    setNotice(null);
    try {
      const r = await stockApi.scan(code);
      if (r.kind === "product") await navigate({ to: "/stock/product/$sku", params: { sku: r.sku } });
      else await navigate({ to: "/stock/shelf", search: { code: r.code, mode: tab === "count" ? "count" : "receive" } });
    } catch (e) {
      setNotice(errorNotice(e));
    }
  };

  const d = today.data;
  return (
    <div className="page stock-narrow">
      <div className="page-head">
        <h1 className="page-title">{t("today.title")}</h1>
        <Button size="icon" variant="ghost" aria-label={t("today.refresh")} onClick={() => void today.refetch()}>
          <IconRefresh />
        </Button>
      </div>
      <ScanBar onScan={(c) => void scan(c)} placeholder={t("today.scan")} autoFocus />
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {today.error ? <Notice tone="danger" title={errorNotice(today.error).title} className="stock-notice" /> : null}
      {d ? (
        <Tabs<TodayTab>
          value={tab}
          onValueChange={(v) => void navigate({ to: "/stock/today", search: { tab: v }, replace: true })}
          label={t("today.queues")}
          items={[
            { value: "receive", label: t("today.tab.receive"), count: d.receipts.length },
            { value: "issue", label: t("today.tab.issue"), count: d.issues.length },
            { value: "count", label: t("today.tab.count"), count: d.counts.due.length },
          ]}
        >
          <TabPanel value="receive" className="queue">
            {d.receipts.length === 0 ? <Empty title={t("today.receive.empty")} lead={t("today.receive.emptyLead")} /> : null}
            {d.receipts.map((r) => (
              <Receipt key={r.id} receipt={r} />
            ))}
          </TabPanel>
          <TabPanel value="issue" className="queue">
            {d.issues.length === 0 ? <Empty title={t("today.issue.empty")} lead={t("today.issue.emptyLead")} /> : null}
            {d.issues.map((r) => (
              <Issue key={r.id} card={r} />
            ))}
          </TabPanel>
          <TabPanel value="count" className="queue">
            {d.counts.pending > 0 ? <p className="w-muted">{t("today.count.pending", { count: d.counts.pending })}</p> : null}
            {d.counts.due.length === 0 ? <Empty title={t("today.count.empty")} lead={t("today.count.emptyLead")} /> : null}
            {d.counts.due.map((c) => (
              <Link key={c.code} to="/stock/shelf" search={{ code: c.code, mode: "count" }} className="queue-row">
                <span className="w-num queue-shelf">{c.code}</span>
                <span className="queue-name">{c.product.name}</span>
                <span className="w-muted queue-meta">{c.lastCount ? t("count.last", { date: formatDate(c.lastCount) }) : t("count.never")}</span>
              </Link>
            ))}
          </TabPanel>
        </Tabs>
      ) : null}
    </div>
  );
}

function Empty({ title, lead }: { title: string; lead: string }) {
  return (
    <div className="queue-empty">
      <p className="queue-empty-title">{title}</p>
      <p className="w-muted">{lead}</p>
    </div>
  );
}

/** Поставка: строки с полкой; нажатие ведёт на полку в «Прийняти». */
function Receipt({ receipt }: { receipt: ReceiptCard }) {
  return (
    <Plate cut="sm" bleed className="queue-card">
      <div className="queue-card-head">
        <span className="w-num queue-doc">{receipt.order}</span>
        <span className={receipt.late ? "queue-late" : "w-muted"}>
          {formatDate(receipt.due)}
          {receipt.late ? ` · ${t("today.late")}` : ""}
        </span>
      </div>
      <div className="queue-sub">{receipt.supplier}</div>
      <ul className="queue-lines">
        {receipt.lines.map((l) => (
          <li key={l.product.sku}>
            {l.shelf ? (
              <Link to="/stock/shelf" search={{ code: l.shelf, mode: "receive" }} className="queue-line">
                <span className="w-num queue-shelf">{l.shelf}</span>
                <span className="queue-name">{l.product.name}</span>
                <span className="w-num queue-qty">{`${formatNumber(l.qty)}\u00a0${l.product.uom}`}</span>
              </Link>
            ) : (
              <div className="queue-line">
                <span className="queue-shelf queue-noshelf">{t("today.noShelf")}</span>
                <span className="queue-name">{l.product.name}</span>
                <span className="w-num queue-qty">{`${formatNumber(l.qty)}\u00a0${l.product.uom}`}</span>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Plate>
  );
}

function Issue({ card }: { card: IssueCard }) {
  return (
    <Link to="/stock/issue/$id" params={{ id: card.id }} className="queue-card queue-card-link w-plate w-cut" data-cut-bleed="">
      <div className="queue-card-head">
        <span className="w-num queue-doc">{card.number}</span>
        <span className={card.late ? "queue-late" : "w-muted"}>
          {formatDate(card.needDate)}
          {card.late ? ` · ${t("today.late")}` : ""}
        </span>
      </div>
      <div className="queue-sub">
        <Badge tone="strong">{card.warehouse.name}</Badge>
        {card.order ? <span className="w-num">{card.order}</span> : null}
        <span className="w-muted">{t("today.issue.lines", { count: card.lines.length })}</span>
      </div>
    </Link>
  );
}
